/**
 * The bed a bedroom is staged around: which one, its frame, the wall at its
 * head and the nightstands beside it.
 *
 * The bed's frame is its own: local +x across, local +z from head to foot
 * (the head is local -z on every corpus bed), with the world mapping of
 * `objectWorldFromLocal` — the stored-rotation convention every builder
 * uses. Positions from `../../bed/dressBed.js` come out in this frame.
 */

const { objectTypeOf } = require('../../ownership');
const { footprintFor } = require('../../../geometry/objectFootprint');
const { objectWorldFromLocal } = require('../../../dorm-styling/plan/shared');
const { wallAxesFor } = require('../../../dorm-styling/dormDecorGeometry');
const {
  objectIsPlaceholder,
} = require('../../../fixture-types/productRepresentation');
const { bedSizeClass } = require('../../bed/dressBed');
const {
  HEAD_WALL_GAP_M,
  HEAD_WALL_PARALLEL,
  NIGHTSTAND_TYPES,
  NIGHTSTAND_MAX_FROM_HEAD_M,
  NIGHTSTAND_SIDE_GAP_M,
  NIGHTSTAND_MAX_WIDTH_M,
  NIGHTSTAND_HEIGHT_M,
} = require('./constants');

const hasBox = (o) => !!(o?.center && o?.dimensions);

/** A point in the bed's frame, from world. Inverse of `objectWorldFromLocal`. */
function bedLocal(bed, p) {
  const r = bed.rotation?.y || 0;
  const c = Math.cos(r);
  const s = Math.sin(r);
  const dx = p.x - bed.center.x;
  const dz = p.z - bed.center.z;
  return { x: dx * c - dz * s, z: dx * s + dz * c };
}

/** The bed's floor plane, world y: the scanned box's bottom. */
const bedBaseY = (bed) => bed.center.y - bed.dimensions.y / 2;

/**
 * Which bed to stage. Every `bed` with a box is a candidate; the largest
 * footprint wins (11 of 64 corpus bed rooms hold more than one). `dorm-*` beds
 * are a different type and never reach here.
 *
 * A TALL bed is staged too. Every corpus bed scanned over 40 in (9 of them,
 * 62-70 in) is 43-60 in wide with nothing under it: a tall headboard, not a
 * bunk or a loft, which are twin-width. The built-in bed takes no height from
 * the scan above its 25 in mattress top, so it becomes a standard bed (the
 * user, 2026-10-30). A real bunk would become one too, for now; the dorm
 * bunk and loft renderers are where it would go once a scan holds one.
 *
 * @returns {{ bed: object|null, skipped: {id:string, reason:string}[] }}
 */
function pickStagedBed(room) {
  const skipped = [];
  let bed = null;
  for (const o of room?.objects || []) {
    if (objectTypeOf(o) !== 'bed' || !hasBox(o)) continue;
    const area = o.dimensions.x * o.dimensions.z;
    if (!bed || area > bed.dimensions.x * bed.dimensions.z) {
      if (bed) skipped.push({ id: bed.id, reason: 'not-the-largest-bed' });
      bed = o;
    } else {
      skipped.push({ id: o.id, reason: 'not-the-largest-bed' });
    }
  }
  return { bed, skipped };
}

/**
 * Did someone pick a catalog product for this bed? Staging never replaces a
 * product the user chose; only a bed still drawn as a placeholder (the stock
 * model or the built-in bed) is swapped for the staged built-in one.
 */
const bedHasPickedProduct = (bed) => !objectIsPlaceholder(bed);

/** The bed's box in metres and its mattress class. */
function bedBoxOf(bed) {
  const box = { x: bed.dimensions.x, y: bed.dimensions.y, z: bed.dimensions.z };
  return { box, size: bedSizeClass(box.x, box.z) };
}

/**
 * The wall at the bed's head: parallel to the headboard, spanning the head's
 * midpoint, and within `HEAD_WALL_GAP_M` of it. Null for a bed standing in
 * the room.
 */
function findHeadWall(room, bed, endpointsById) {
  const head = objectWorldFromLocal(bed, 0, -bed.dimensions.z / 2);
  const r = bed.rotation?.y || 0;
  const across = { x: Math.cos(r), z: -Math.sin(r) };
  let best = null;
  for (const wall of room?.walls || []) {
    const axes = wallAxesFor(wall, endpointsById)?.axes;
    if (!axes) continue;
    const par = Math.abs(axes.wallDir.x * across.x + axes.wallDir.z * across.z);
    if (par < HEAD_WALL_PARALLEL) continue;
    const t =
      (head.x - axes.start.x) * axes.wallDir.x +
      (head.z - axes.start.z) * axes.wallDir.z;
    if (t < 0 || t > axes.wallLen) continue;
    const gap =
      Math.abs(
        (head.x - axes.start.x) * axes.wallDir.z -
          (head.z - axes.start.z) * axes.wallDir.x
      ) -
      (wall.dimensions?.z || 0) / 2;
    if (gap > HEAD_WALL_GAP_M) continue;
    if (!best || gap < best.gap) best = { wall, axes, gap };
  }
  return best;
}

/**
 * The bed-frame box of an object's footprint: x and z extents.
 * Null when it has no footprint.
 */
function bedFrameBox(bed, o) {
  const f = footprintFor(
    o.center,
    o.dimensions.x,
    o.dimensions.z,
    o.rotation?.y || 0
  );
  if (!f) return null;
  const pts = f.map((p) => bedLocal(bed, p));
  const xs = pts.map((p) => p.x);
  const zs = pts.map((p) => p.z);
  return {
    x0: Math.min(...xs),
    x1: Math.max(...xs),
    z0: Math.min(...zs),
    z1: Math.max(...zs),
  };
}

/**
 * The nightstands beside the bed's head, one per side at most (the nearest to
 * the head on that side). See `NIGHTSTAND_*` for the measured rule.
 *
 * Each comes back with its top as a box in the bed frame, the shape
 * `dressNightstand` takes: `outer` / `inner` x edges, `back` / `front` z
 * edges, and `top` above the BED's floor plane.
 *
 * @returns {{ side: -1|1, object: object, box: object }[]}  left first
 */
function findNightstands(room, bed) {
  const halfW = bed.dimensions.x / 2;
  const headZ = -bed.dimensions.z / 2;
  const base = bedBaseY(bed);
  const bySide = new Map();
  for (const o of room?.objects || []) {
    if (o === bed || !hasBox(o)) continue;
    if (!NIGHTSTAND_TYPES.has(objectTypeOf(o))) continue;
    const h = o.dimensions.y;
    if (h < NIGHTSTAND_HEIGHT_M[0] || h > NIGHTSTAND_HEIGHT_M[1]) continue;
    const b = bedFrameBox(bed, o);
    if (!b) continue;
    const cx = (b.x0 + b.x1) / 2;
    const cz = (b.z0 + b.z1) / 2;
    if (b.x1 - b.x0 > NIGHTSTAND_MAX_WIDTH_M) continue;
    if (cz - headZ > NIGHTSTAND_MAX_FROM_HEAD_M) continue;
    const side = cx < 0 ? -1 : 1;
    const gap = side > 0 ? b.x0 - halfW : -halfW - b.x1;
    if (gap < NIGHTSTAND_SIDE_GAP_M[0] || gap > NIGHTSTAND_SIDE_GAP_M[1])
      continue;
    const top = o.center.y + h / 2 - base;
    const found = {
      side,
      object: o,
      box: {
        outer: side > 0 ? b.x1 : b.x0,
        inner: side > 0 ? b.x0 : b.x1,
        back: b.z0,
        front: b.z1,
        top,
      },
      fromHead: cz - headZ,
    };
    const prev = bySide.get(side);
    if (!prev || found.fromHead < prev.fromHead) bySide.set(side, found);
  }
  return [-1, 1].filter((s) => bySide.has(s)).map((s) => bySide.get(s));
}

module.exports = {
  bedLocal,
  bedBaseY,
  pickStagedBed,
  bedHasPickedProduct,
  bedBoxOf,
  findHeadWall,
  findNightstands,
  bedFrameBox,
};
